import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe2, Map, FileText, Users, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { PageHeader, SectionHeader, Loading, categoryLabel } from '../components/Shared';

export default function CitywideView() {
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState(null);
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/network/stats').then((r) => setStats(r.data)),
      api.get('/network/content').then((r) => setContent(r.data)),
      api.get('/network/supporters').then((r) => setSupporters(r.data || [])),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading || !stats || !content) return <Loading />;

  return (
    <div data-testid="citywide-page">
      <PageHeader kicker="CITYWIDE CONCEPT" title={content.citywide_title} subtitle={content.citywide_message} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {content.citywide_preview !== false && (
          <Badge variant="outline" className="border-[#C9A84C] bg-[#C9A84C]/10 text-[#7A5C12] font-bold" data-testid="citywide-preview-badge">
            Early preview. Grows as more wards participate
          </Badge>
        )}

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="citywide-stats">
          {[
            ['Participating wards', stats.participating_wards],
            ['Total issues tracked', stats.total_tracked],
            ['Resolved', stats.resolved],
            ['Still open', stats.open],
          ].map(([l, v]) => (
            <Card key={l} className="card-lift"><CardContent className="p-4 text-center">
              <p className="text-3xl font-black text-[#1a2744]">{v}</p>
              <p className="text-xs font-bold text-slate-500 mt-1">{l}</p>
            </CardContent></Card>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card data-testid="citywide-categories">
            <CardContent className="p-6">
              <BarChart3 className="h-6 w-6 text-[#C9A84C]" />
              <h3 className="font-serif font-black text-[#1a2744] text-lg mt-3">Top issue categories</h3>
              <div className="mt-3 space-y-2">
                {stats.top_categories.length === 0 && <p className="text-sm text-slate-500">No data yet.</p>}
                {stats.top_categories.map((c) => (
                  <div key={c.category} className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-700">{categoryLabel(c.category)}</span>
                    <span className="font-black text-[#1a2744]">{c.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="citywide-by-ward">
            <CardContent className="p-6">
              <Users className="h-6 w-6 text-[#C9A84C]" />
              <h3 className="font-serif font-black text-[#1a2744] text-lg mt-3">Ward-by-ward view</h3>
              <div className="mt-3 space-y-2">
                {stats.by_ward.map((w) => (
                  <div key={w.tracker_slug} className="flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-700">{(w.tracker_slug || 'ward-8').replace('-', ' ').replace(/\b\w/g, (m) => m.toUpperCase())}</span>
                    <span className="font-black text-[#1a2744]">{w.count} tracked</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-3">More wards appear here as candidates join the network. Listings are opt-in only.</p>
            </CardContent>
          </Card>
        </div>

        {supporters.length > 0 && (
          <div className="mt-10" data-testid="citywide-supporters">
            <SectionHeader kicker="OPT-IN ONLY" title="Participating candidates" />
            <div className="flex flex-wrap gap-2">
              {supporters.map((s) => (
                <Badge key={s.id} variant="outline" className="border-[#1a2744]/30 text-[#1a2744] font-bold px-3 py-1.5">
                  {s.name} {s.office === 'mayor' ? '(Mayor)' : s.ward ? `(Ward ${s.ward})` : ''}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-3">
          <Button asChild className="h-12 px-5 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press justify-center" data-testid="citywide-map-btn">
            <Link to="/map"><Map className="h-4 w-4 mr-1.5" /> Combined Public Map</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-5 border-[#1a2744] text-[#1a2744] font-bold btn-press justify-center" data-testid="citywide-reports-btn">
            <Link to="/newsroom"><FileText className="h-4 w-4 mr-1.5" /> Monthly Reports in the Newsroom</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-5 border-[#1a2744] text-[#1a2744] font-bold btn-press justify-center" data-testid="citywide-network-btn">
            <Link to="/tracker-network"><Globe2 className="h-4 w-4 mr-1.5" /> Join the Network</Link>
          </Button>
        </div>

        <p className="mt-8 text-xs text-slate-500 leading-relaxed max-w-2xl" data-testid="citywide-disclaimer">
          All figures are privacy-safe aggregates from the Public Issue Tracker App (PITA). They are not official City of Kawartha Lakes statistics. No names, contact details, or exact addresses are ever shown publicly.
        </p>
      </div>
    </div>
  );
}
